import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  addWrapper,
  flowMax,
} from 'ad-hok'
import { ComponentProps, FC } from 'react'
import { createUseStyles } from 'react-jss'
import { typedAs } from 'shared/src/utils/typedAs'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import cx from 'classnames'

import { Field } from './Field'
import { ResourceName } from './resource'
import { addResourceWithName } from 'utils/addResourceWithName'
import { addFormContext } from './formContext'
import { ResourceEdit } from './ResourceEdit'
import { addCmsLoadingAndErrorIndicators } from 'utils/addCmsLoadingAndErrorIndicators'
import Icon from './Icon'

const styles = {
  editButton: cx(
    'ml-5',
    'cursor-pointer',
    'text-rodeo-secondary1/56',
    'hover:text-rodeo-secondary1/96'
  ),
}

const useStyles = createUseStyles({
  form: {
    marginTop: 10,
  },
  option: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  thumbnailContainer: {
    height: 40,
    width: 40,
    display: 'flex',
    alignItems: 'center',
  },
  thumbnail: {
    objectFit: 'cover',
    marginRight: 10,
    height: '100%',
    width: '100%',
  },
})

interface Selecting {
  type: 'selecting'
}

interface Creating {
  type: 'creating'
  query?: string
}

interface Editing {
  type: 'editing'
  id: number
}

type State = Selecting | Creating | Editing

interface Props extends ComponentProps<typeof Field> {
  resourceName: ResourceName
  selectedId: number | undefined
  onChange: (id: number | undefined) => void
  disableCreate?: boolean
  filter?: (value: any) => boolean
  disabled?: boolean
}

export const ResourceEditField: FC<Props> = flowMax(
  addDisplayName('ResourceEditField'),
  addResourceWithName(({ resourceName }) => resourceName),
  addState('state', 'setState', typedAs<State>({ type: 'selecting' })),
  addDataLoadingFromPromise(
    'value',
    ({ selectedId, resource: { api } }) =>
      selectedId ? api.get(selectedId) : Promise.resolve(undefined),
    ['resource', 'state.type']
  ),
  addCmsLoadingAndErrorIndicators('value'),
  addFormContext,
  addHandlers({
    onEdit:
      ({ setState, setFormDisabled }) =>
      (id: number) => {
        setState({ type: 'editing', id })
        setFormDisabled(true)
      },
    onCreate:
      ({ setState, setFormDisabled }) =>
      (query?: string) => {
        setState({ type: 'creating', query })
        setFormDisabled(true)
      },
    onSaved:
      ({ setState, onChange, setFormDisabled }) =>
      (model: any) => {
        setState({ type: 'selecting' })
        onChange(model.id)
        setFormDisabled(false)
      },
    onCancel:
      ({ setState, setFormDisabled }) =>
      () => {
        setState({ type: 'selecting' })
        setFormDisabled(false)
      },
    onCreated:
      ({ setState, setValue, onChange, setFormDisabled }) =>
      (model: any) => {
        setState({ type: 'selecting' })
        setValue(model)
        onChange(model.id)
        setFormDisabled(false)
      },
  }),
  addWrapper((render, { id, label, error, state, suggestion }) => (
    <Field
      id={id}
      label={label}
      error={error}
      ignoreDisabled={state.type !== 'selecting'}
      suggestion={suggestion}
    >
      {render()}
    </Field>
  )),
  addProps(({ value, resource }) => ({
    displayData: value
      ? {
          value: value.id,
          image: value.image,
          label: resource.title(value),
        }
      : undefined,
  })),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    state,
    selectedId,
    resource,
    onEdit,
    onSaved,
    onCancel,
    onCreated,
    onCreate,
    classes,
    displayData,
  }) => {
    if (state.type === 'selecting') {
      return (
        <div className={classes.option}>
          {displayData ? (
            displayData.image && (
              <>
                <div className={classes.thumbnailContainer}>
                  <img
                    src={displayData.image.thumbnail}
                    alt=""
                    className={classes.thumbnail}
                  />
                </div>
                <span> {displayData.label}</span>
              </>
            )
          ) : (
            <div> </div>
          )}
          {selectedId && (
            <Icon
              name="edit"
              onClick={() => onEdit(selectedId)}
              className={styles.editButton}
            />
          )}
          {!selectedId && onCreate && (
            <Icon
              name="add"
              onClick={() => onCreate()}
              className={styles.editButton}
            />
          )}
        </div>
      )
    } else if (state.type === 'editing') {
      return (
        <div className={classes.form}>
          <ResourceEdit
            resource={resource}
            resourceId={selectedId}
            embedded
            onSave={onSaved}
            onCancel={onCancel}
          />
        </div>
      )
    } else {
      return (
        <div className={classes.form}>
          <ResourceEdit
            resource={resource}
            embedded
            onSave={onCreated}
            onCancel={onCancel}
          />
        </div>
      )
    }
  }
)
